import React, { useEffect, useState, useCallback } from "react";
import AllCurrency from "../../json/Common-Currency.json";
import FormInput from "Common/Custom/FormInput";
import FormSelect from "Common/Custom/FormSelect";
import FormSubmitButton from "Common/Custom/FormSubmitButton";
import PageBody from "Common/Custom/PageBody";
import { HeadingOne } from "Common/Custom/HeadingOne";
import Switch from "Common/Custom/Switch";

/**
 * CurrencyEditForm Component
 * Specialized form for updating system currency configurations.
 */
const CurrencyEditForm = ({
  fields: formdata = [],
  values,
  errors,
  handleChange,
  handleSubmit,
  loading,
  title = "Update Currency",
  setValues,
}) => {
  const [currencyList, setCurrencyList] = useState([]);

  // Load global currency reference data
  useEffect(() => {
    if (AllCurrency && typeof AllCurrency === "object") {
      const list = Object.keys(AllCurrency).map((key) => AllCurrency[key]);
      setCurrencyList(list);
    }
  }, []);

  /**
   * Selection handler that facilitates auto-population of symbols
   * and country names.
   */
  const handleCurrencyChange = useCallback((e) => {
    handleChange(e);
    const { value } = e.target;

    if (value !== "Other") {
      const selected = currencyList.find((c) => c.code === value);
      if (selected) {
        // Extract country name from currency name (e.g., "US Dollar" -> "United States")
        let countryName = "";
        if (selected.name.includes("US Dollar")) countryName = "United States";
        else if (selected.name.includes("Canadian Dollar")) countryName = "Canada";
        else if (selected.name.includes("Euro")) countryName = "European Union";
        else if (selected.name.includes("British Pound")) countryName = "United Kingdom";
        else if (selected.name.includes("Japanese Yen")) countryName = "Japan";
        else if (selected.name.includes("Chinese Yuan")) countryName = "China";
        else if (selected.name.includes("Indian Rupee")) countryName = "India";
        else if (selected.name.includes("Australian Dollar")) countryName = "Australia";
        else if (selected.name.includes("Swiss Franc")) countryName = "Switzerland";
        else if (selected.name.includes("Hong Kong Dollar")) countryName = "Hong Kong";
        else if (selected.name.includes("Singapore Dollar")) countryName = "Singapore";
        else if (selected.name.includes("Swedish Krona")) countryName = "Sweden";
        else if (selected.name.includes("New Zealand Dollar")) countryName = "New Zealand";
        else if (selected.name.includes("Mexican Peso")) countryName = "Mexico";
        else if (selected.name.includes("Norwegian Krone")) countryName = "Norway";
        else if (selected.name.includes("South Korean Won")) countryName = "South Korea";
        else if (selected.name.includes("Turkish Lira")) countryName = "Turkey";
        else if (selected.name.includes("Russian Ruble")) countryName = "Russia";
        else if (selected.name.includes("Brazilian Real")) countryName = "Brazil";
        else if (selected.name.includes("South African Rand")) countryName = "South Africa";
        else if (selected.name.includes("Thai Baht")) countryName = "Thailand";
        else if (selected.name.includes("Indonesian Rupiah")) countryName = "Indonesia";
        else if (selected.name.includes("Malaysian Ringgit")) countryName = "Malaysia";
        else if (selected.name.includes("Philippine Peso")) countryName = "Philippines";
        else if (selected.name.includes("Chilean Peso")) countryName = "Chile";
        else if (selected.name.includes("Colombian Peso")) countryName = "Colombia";
        else if (selected.name.includes("Peruvian Nuevo Sol")) countryName = "Peru";
        else if (selected.name.includes("Ukrainian Hryvnia")) countryName = "Ukraine";
        else if (selected.name.includes("Polish Zloty")) countryName = "Poland";
        else if (selected.name.includes("Danish Krone")) countryName = "Denmark";
        else if (selected.name.includes("Czech Republic Koruna")) countryName = "Czech Republic";
        else if (selected.name.includes("Hungarian Forint")) countryName = "Hungary";
        else if (selected.name.includes("Romanian Leu")) countryName = "Romania";
        else if (selected.name.includes("Israeli New Sheqel")) countryName = "Israel";
        else if (selected.name.includes("Saudi Riyal")) countryName = "Saudi Arabia";
        else if (selected.name.includes("UAE Dirham")) countryName = "United Arab Emirates";
        else if (selected.name.includes("Qatari Rial")) countryName = "Qatar";
        else if (selected.name.includes("Kuwaiti Dinar")) countryName = "Kuwait";
        else if (selected.name.includes("Bahraini Dinar")) countryName = "Bahrain";
        else if (selected.name.includes("Omani Rial")) countryName = "Oman";
        else if (selected.name.includes("Jordanian Dinar")) countryName = "Jordan";
        else if (selected.name.includes("Lebanese Pound")) countryName = "Lebanon";
        else if (selected.name.includes("Egyptian Pound")) countryName = "Egypt";
        else if (selected.name.includes("Moroccan Dirham")) countryName = "Morocco";
        else if (selected.name.includes("Nigerian Naira")) countryName = "Nigeria";
        else if (selected.name.includes("Kenyan Shilling")) countryName = "Kenya";
        else if (selected.name.includes("Ghanaian Cedi")) countryName = "Ghana";
        else if (selected.name.includes("Pakistani Rupee")) countryName = "Pakistan";
        else if (selected.name.includes("Bangladeshi Taka")) countryName = "Bangladesh";
        else if (selected.name.includes("Sri Lankan Rupee")) countryName = "Sri Lanka";
        else if (selected.name.includes("Nepalese Rupee")) countryName = "Nepal";
        else if (selected.name.includes("Myanma Kyat")) countryName = "Myanmar";
        else if (selected.name.includes("Cambodian Riel")) countryName = "Cambodia";
        else if (selected.name.includes("Vietnamese Dong")) countryName = "Vietnam";
        else {
          // Default: try to extract from the first part of the name
          const parts = selected.name.split(' ');
          if (parts.length > 1) {
            countryName = parts.slice(0, -1).join(' ');
          } else {
            countryName = selected.name;
          }
        }

        setValues((prev) => ({
          ...prev,
          curr_icon: selected.symbol || "",
          country_name: countryName || ""
        }));
      }
    } else {
      setValues((prev) => ({ ...prev, curr_icon: "", country_name: "" }));
    }
  }, [currencyList, handleChange, setValues]);

  return (
    <PageBody
      onSubmit={handleSubmit}
      headerchildren={<HeadingOne>{title}</HeadingOne>}
    >
      {formdata.map((field) => {
        // Selection for Currency Type (Standard vs Other)
        if (field.name === "currencyname") {
          const options = [
            ...currencyList.map((c) => ({
              label: `${c.name} (${c.code})`,
              value: c.code,
            })),
            { label: "Other", value: "Other" },
          ];

          return (
            <FormSelect
              key={field.name}
              field={field}
              options={options}
              value={values[field.name]}
              handleChange={handleCurrencyChange}
              error={errors[field.name]}
            />
          );
        }

        // Position Configuration (Left vs Right)
        if (field.name === "position") {
          const options = [
            { label: "Left (e.g. $100)", value: 0 },
            { label: "Right (e.g. 100€)", value: 1 },
          ];

          return (
            <FormSelect
              key={field.name}
              field={field}
              options={options}
              value={String(values[field.name] ?? "0")}
              handleChange={handleChange}
              error={errors[field.name]}
            />
          );
        }

        // Country Name Input (Auto-filled for standard currencies)
        if (field.name === "country_name") {
          const isStandard = values.currencyname && values.currencyname !== "Other";
          return (
            <FormInput
              key={field.name}
              field={field}
              value={values[field.name]}
              handleChange={handleChange}
              error={errors[field.name]}
              readOnly={isStandard}
              placeholder={isStandard ? "Country auto-filled" : "Enter country name"}
            />
          );
        }

        // Currency Symbol Display/Input
        if (field.name === "curr_icon") {
          const isStandard = values.currencyname && values.currencyname !== "Other";
          return (
            <FormInput
              key={field.name}
              field={field}
              value={values[field.name]}
              handleChange={handleChange}
              error={errors[field.name]}
              readOnly={isStandard}
              placeholder={isStandard ? "Symbol auto-filled" : "Enter custom symbol"}
            />
          );
        }

        // Default Status Toggle
        if (field.name === "is_default") {
          return (
            <Switch
              key={field.name}
              field={field}
              values={values[field.name]}
              handleChange={handleChange}
              ValueOne={0}
              ValueTwo={1}
              LabelOne="No"
              LabelTwo="Yes"
              error={errors[field.name]}
            />
          );
        }

        // Standard Text/Number inputs
        return (
          <FormInput
            key={field.name}
            field={field}
            value={values[field.name]}
            handleChange={handleChange}
            error={errors[field.name]}
          />
        );
      })}

      <FormSubmitButton
        label="Update Currency"
        loading={loading}
        colSize="col-12"
        align="end"
      />
    </PageBody>
  );
};

export default CurrencyEditForm;
